import {
  buildCommunityGuidelinesURI,
  buildPrivacyURI,
  useConfig,
  useValidation,
  type Rules,
} from '@seek/libs-shared';
import { Stack, Text, TextLink } from 'braid-design-system';
import type { SearchCompanyProfileResult } from '../../../shared/services/companyProfileService/types';
import {
  CURRENT_PAGE_OVERALL,
  trackContinuePressed,
} from '../../../shared/tracking/writeAReview/trackingEvents';
import { ReviewPageLayoutNoStepper } from '../../components/ReviewPageLayout/ReviewPageLayoutNoStepper';
import type { Rating } from '../../components/StarSelector/StarSelector';
import type { CompanyNameValueV2 } from '../../questions/AutoSuggestionQuestions/CompanyNameQuestion/CompanyNameQuestionV2';
import { OverallRatingQuestion } from '../../questions/RatingQuesions/OverallRatingQuestion';
import { OverallExperienceDetailsQuestion } from '../../questions/SimpleQuestions/OverallExperienceDetailsQuestion';
import { type PageProps, type ReviewPayload } from '../../type';
import { validationRules } from '../../validation/validationRules';
import { CompanyNameField } from '../OverallRatingPage';

type Props = PageProps & {
  isSubmitting: boolean;
  searchCompanyProfiles: (query: string) => Promise<SearchCompanyProfileResult>;
  hideCompanyNameSearch?: boolean;
};

export const ReviewCompanyPage = ({
  review,
  reviewFormContext,
  searchCompanyProfiles,
  hideCompanyNameSearch,
  setter,
  pageNumber,
  isSubmitting,
  onSubmit,
}: Props) => {
  const config = useConfig();

  const fields = {
    companyName: review?.companyName,
    overallRating: review?.overallRating,
    overallDescription: review?.overallDescription,
    companyId: review?.companyId,
  };

  const { companyName, overallRating, overallDescription, companyId } =
    validationRules(config.language);

  const rules = {
    companyName,
    overallRating,
    overallDescription,
    companyId,
  } as Rules<typeof fields>;

  const {
    getTone,
    getMessage,
    handleValueChangeWithValidation,
    handleUpdateMultipleValuesWithValidation,
    validateWithErrors,
  } = useValidation<ReviewPayload>(fields, rules);

  const handleOverallRatingChange = (value: Rating) => {
    handleValueChangeWithValidation({
      setter,
      validatorProperty: 'overallRating',
      previousValue: review,
    })(value);
  };

  const handleOnContinue = () => {
    const { isValid } = validateWithErrors();

    if (onSubmit) {
      onSubmit();
    }

    trackContinuePressed({
      config,
      context: reviewFormContext,
      validation: isValid ? 'success' : 'mandatory_questions_not_answered',
      currentPage: CURRENT_PAGE_OVERALL,
      variant: reviewFormContext?.variant ?? '1.0.0',
    });

    if (!isValid) {
      return;
    }
  };

  const onSearchCompanyProfiles = async (query: string) => {
    return searchCompanyProfiles(query);
  };

  const handleCompanyNameFieldChange = (value: CompanyNameValueV2) => {
    handleUpdateMultipleValuesWithValidation({
      setter,
      previousValue: review,
    })({
      companyId: value.companyId,
      organisationId: value.organisationId,
      companyName: value.companyName,
      companyProfilePublished: value.published,
      companyNameFieldMode: value.mode,
    });
  };

  const getCompanyNameFieldErrorMessage = () => {
    const companyNameMessage = getMessage('companyName');
    return companyNameMessage.length > 0
      ? companyNameMessage
      : getMessage('companyId');
  };

  return (
    <ReviewPageLayoutNoStepper
      progress={pageNumber}
      heading={
        review?.companyName
          ? `Review working at ${review?.companyName}`
          : 'Write a company review'
      }
      hideStepper={true}
      onContinue={handleOnContinue}
      isContinueLoading={isSubmitting}
      subtitle="Your anonymous review may be shown on our website."
      logo={review?.branding?.logoImageUrl}
    >
      <Stack space="small">
        <Stack space="large">
          <CompanyNameField
            handleCompanyNameFieldChange={handleCompanyNameFieldChange}
            onSearchCompanyProfiles={onSearchCompanyProfiles}
            review={review}
            hideCompanyNameSearch={hideCompanyNameSearch}
            errorMessage={getCompanyNameFieldErrorMessage()}
          />
          <OverallRatingQuestion
            onChange={handleOverallRatingChange}
            value={review?.overallRating}
            message={getMessage('overallRating')}
            title="Rate your overall experience"
          />
          <OverallExperienceDetailsQuestion
            onChange={handleValueChangeWithValidation({
              setter,
              validatorProperty: 'overallDescription',
              previousValue: review,
            })}
            value={review?.overallDescription}
            tone={getTone('overallDescription')}
            message={getMessage('overallDescription')}
            isRequired={false}
            description="Describe your overall experience, including both the positives and the challenges."
          />
        </Stack>
        <Text size="small">
          Personal information submitted will follow our{' '}
          <TextLink
            href={buildPrivacyURI({
              country: config.country,
              environment: config.environment,
              language: config.language,
            })}
            weight="weak"
          >
            Privacy Statement
          </TextLink>
          . By submitting, you agree to our{' '}
          <TextLink
            href={buildCommunityGuidelinesURI({
              locale: config.locale,
              zone: config.zone,
              experience: config.experience,
            })}
            weight="weak"
          >
            Reviews Guidelines
          </TextLink>
          .
        </Text>
      </Stack>
    </ReviewPageLayoutNoStepper>
  );
};
